import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { IBusiness } from "@/business/business";
import { GOOGLE_BUSINESS_CONNECTION_STATE } from "@/business/constants";
import { GoogleBusinessConnectionModal } from "@/business-settings/business/google-business-connection-modal";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwner } from "@/user/utils";
import GoogleLogo from "@/assets/images/google-logo.svg";

type ModalState = "initial" | "location_selection" | "success" | "error";

interface GoogleBusinessPostOnboardingModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  businessId?: string;
  business?: IBusiness;
  onComplete?: () => void;
}

export const GoogleBusinessPostOnboardingModal = ({
  isOpen,
  onOpenChange,
  businessId,
  business,
  onComplete,
}: GoogleBusinessPostOnboardingModalProps) => {
  const { data: user } = useGetMyUser();
  const [isLoading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>("initial");
  const [showLocationModal, setShowLocationModal] = useState(false);

  const canConnectAccount = isAdminOrOwner(user?.role);
  const isConnected =
    business?.google_business_connection_state ===
    GOOGLE_BUSINESS_CONNECTION_STATE.connected;

  useEffect(() => {
    if (isOpen && isConnected) {
      setModalState("location_selection");
    } else if (isOpen) {
      setModalState("initial");
    }
  }, [isOpen, isConnected]);

  const handleGoogleBusinessConnect = async () => {
    if (!businessId || !user?.email) {
      toast.error("Missing required information", {
        description: "Business ID and user email are required",
      });
      return;
    }

    setIsLoading(true);

    try {
      const apiBase = import.meta.env.VITE_API_URL;
      if (!apiBase) {
        toast.error("Couldn't connect to Google Business", {
          description: "Refresh the page and try again.",
        });
        setIsLoading(false);
        return;
      }

      const params = new URLSearchParams({
        state: JSON.stringify({
          business_id: businessId,
          user_email: user.email,
          redirect_to: "post-onboarding",
        }),
      });

      onOpenChange(false);
      window.location.href = `${apiBase}/auth/google_oauth2?${params.toString()}`;
    } catch (error) {
      console.error("Google OAuth initiation error:", error);
      toast.error("Failed to initiate Google Business connection", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
      setIsLoading(false);
      setModalState("error");
    }
  };

  const handleLocationModalClose = () => {
    setShowLocationModal(false);
    setModalState("success");
  };

  const handleComplete = () => {
    onComplete?.();
    onOpenChange(false);
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  const handleRetry = () => {
    setModalState("initial");
  };

  if (!canConnectAccount && modalState === "initial") {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Connect Google Business</DialogTitle>
          </DialogHeader>
          <Stack align="start" gap={6} className="w-full">
            <Text tone="muted">
              Only managers and admins can connect Google Business accounts.
            </Text>
          </Stack>
          <DialogFooter>
            <Button type="button" onClick={handleClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (showLocationModal && business) {
    return (
      <GoogleBusinessConnectionModal
        business={business}
        onClose={handleLocationModalClose}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={modalState !== "success"}
      >
        <DialogHeader>
          <DialogTitle>Connect Google Business</DialogTitle>
        </DialogHeader>

        <Stack align="start" gap={4} className="w-full">
          {modalState === "initial" && (
            <>
              <Stack
                className="w-full"
                gap={4}
                align="center"
                justify="center"
                direction="row"
              >
                <img src={GoogleLogo} alt="Google" className="h-8 w-8" />
                <Text className="text-center">
                  Connect your Google Business account
                </Text>
              </Stack>

              <Text tone="muted" className="text-center">
                Connect your Google Business account so we can analyze your
                reviews and provide more insights.
              </Text>
            </>
          )}

          {modalState === "location_selection" && (
            <>
              <Stack className="w-full" gap={4} align="center">
                <CheckCircleIcon className="h-10 w-10 text-success" />
              </Stack>
              <Text className="text-center">
                Google Business account connected successfully.
              </Text>
              <Text tone="muted" className="text-center">
                Now select your Google Business locations to complete the setup.
              </Text>
            </>
          )}

          {modalState === "success" && (
            <>
              <Stack className="w-full" gap={4} align="center">
                <CheckCircleIcon className="h-10 w-10 text-success" />
              </Stack>
              <Text className="text-center">Setup complete.</Text>
              <Text tone="muted" className="text-center">
                Your Google Business account has been connected and locations
                have been added.
              </Text>
            </>
          )}

          {modalState === "error" && (
            <>
              <Alert color="danger">
                <WarningIcon aria-hidden />
                <AlertDescription>
                  We were unable to connect to your Google Business account.
                  Please try again.
                </AlertDescription>
              </Alert>
            </>
          )}
        </Stack>

        <DialogFooter>
          {modalState === "initial" && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Skip for now
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleGoogleBusinessConnect}
                disabled={!businessId || !user?.email || isLoading}
                loading={isLoading}
              >
                {!isLoading && (
                  <img
                    src={GoogleLogo}
                    alt="Google"
                    className="h-5 w-5"
                    data-icon="inline-start"
                  />
                )}
                Connect Google Business
              </Button>
            </>
          )}

          {modalState === "location_selection" && (
            <>
              <Button type="button" variant="outline" onClick={handleComplete}>
                Skip for now
              </Button>
              <Button type="button" onClick={() => setShowLocationModal(true)}>
                Select Locations
              </Button>
            </>
          )}

          {modalState === "success" && (
            <Stack className="w-full" gap={4} align="center">
              <Button type="button" onClick={handleComplete}>
                Continue
              </Button>
            </Stack>
          )}

          {modalState === "error" && (
            <>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="button" onClick={handleRetry}>
                Try again
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
